/**
 * `PairedBars` — two bars per row (e.g. current vs. attainable yield), sharing one scale
 * and one unit. The default pair is grey then the brand accent; a caller whose two series
 * carry a direction of their own (UC2's land-cover gain and loss) passes `colors`, because
 * drawing a loss in the positive green is the chart saying the opposite of the data. Hand-built SVG in the same
 * register as `RankedBarChart`/`HistogramChart` (the repo forbids chart libraries):
 * viewBox-scaled, `role="img"` with a full plain-text readout, a legend below naming both
 * series.
 *
 * Scaling round, 3 October 2026: a row may carry a **third** leg (`c`), for a reading that
 * belongs beside the other two rather than above them — UC2's greening check next to its
 * farmland and cover checks. The row grows by one bar's height when any row has one, so a
 * two-leg chart is laid out exactly as it was before.
 *
 * Layout round, 2 October 2026: the label column, the track and the value column are
 * `DimensionBars`' own widths, left-aligned labels and a right-aligned value — two charts
 * stacked in one panel now line up down the same three columns instead of each setting
 * its own margins.
 */
import { useId, useRef } from 'react';

import { colonSep, fmtNum } from '../../../lib/format';
import { NEUTRAL_COLOR } from '../uc1Constants';
import { DRIVER_BAND_COLOR as BAND_COLOR } from '../uc2Constants';
import { LABEL_COL_W, ROW_LABEL_PX, rowLabelColumn, valueWidth } from './labels';
import { LABEL_LINE_H, VALUE_HALO } from '../../../components/charts/labels';
import { useChartMarks } from '../../../components/charts/useChartMarks';
import { useIntelWidth } from './layout';
import { RowLabel } from '../../../components/charts/RowLabel';
import { AxisLabels } from '../../../components/charts/AxisLabels';
import { useAxisTitles } from '../../../components/charts/axisContext';
import { axisBands } from '../../../components/charts/scale';

export const PB_ROW_H = 20;
export const PB_BAR_H = 8;
export const PB_ROW_GAP = 10;
/** The label column, `DimensionBars`' own, so the two charts line up down a panel. */
export const PB_LABEL_W = LABEL_COL_W;
/** Gap between the label column and the bar track. */
export const PB_LABEL_GAP = 8;
/** The value column, right-aligned on the chart's right edge. */
export const PB_VALUE_W = 64;
/** Gap between the track's end and the value column. */
export const PB_VALUE_PAD = 8;
/** The width the chart is laid out at where its box cannot be measured (#749: it measures). */
export const PB_W = 394;
/** Bar track width at the nominal value column — the widest a bar can ever be. */
export const PB_BAR_W = PB_W - PB_LABEL_W - PB_LABEL_GAP - PB_VALUE_W - PB_VALUE_PAD;
export const PB_PAD_TOP = 6;
export const PB_MIN_H = 40;

// The one neutral grey, from `uc1Constants.ts`. Copied by hand until 3 Oct 2026, when
// the client had it darkened and every copy went stale in a different place.
const A_COLOR = NEUTRAL_COLOR;
const B_COLOR = 'var(--brand)';
/** The marker tick's default ink — the chart's own foreground, so it reads over both bars. */
const MARKER_COLOR = 'var(--ink)';

/** The third leg's default ink, for a caller that colours no row of its own. */
const C_COLOR = NEUTRAL_COLOR;

/** Gap between one leg and the next, inside a row. */
const LEG_GAP = 3;

/**
 * The value column is `PB_VALUE_W` **or wider**, whichever the longest value text needs —
 * `DimensionBars`' own rule. UC2's practices rows print "−2.0 points" there, which 64 px
 * does not hold: the longest bar ran under the digits.
 */
function valueColumnW(rows: PairedBarsRow[], w: number): number {
  const longest = rows.reduce(
    (n, row) => Math.max(n, valueWidth(row.valueText ?? valueOf(row))),
    0,
  );
  return Math.max(PB_VALUE_W, Math.min(w / 2, Math.ceil(longest + 4)));
}

/** The right-hand column's default text: `a / b`. */
function valueOf(row: PairedBarsRow): string {
  return row.a == null && row.b == null
    ? '-'
    : `${row.a == null ? '-' : fmtNum(row.a, 1)} / ${row.b == null ? '-' : fmtNum(row.b, 1)}`;
}

export interface PairedBarsRow {
  id: string;
  label: string;
  a: number | null;
  b: number | null;
  /**
   * What the right-hand column prints instead of the default `a / b`.
   *
   * UC2's practices chart prints the **difference** there ("+1.4 points") rather than the
   * two numbers again: the two bars already carry them, and a reader comparing four rows
   * is comparing four differences. The readout keeps both values whatever this says, so
   * nothing is lost to a screen reader.
   */
  valueText?: string;
  /** Plain words appended to this row's own readout, after its numbers. */
  note?: string;
  /**
   * A third reading on the same scale, drawn as a tick across both bars — UC2's clicked
   * watershed against the two classes it is being compared with. Not a bar: it is one
   * place, not a group, and a third bar would invite reading it as a third category.
   */
  marker?: number | null;
  /**
   * This row's own two inks, overriding the chart-wide pair. UC2's drivers block colours
   * each bar by the engine's own verdict on that reading rather than by which series it
   * belongs to, so the colour says "this check passed" and the position says "this is the
   * farmland one".
   */
  aColor?: string;
  bColor?: string;
  /**
   * Hatch over the bar — the chart's "nothing to judge here", the same mark the map draws
   * over a not-assessed unit. Drawn on top of the ink, so a hatched bar still has a length.
   */
  aHatched?: boolean;
  bHatched?: boolean;
  /**
   * A third leg on the same scale, drawn under the other two. Present on **no** chart
   * written before the scaling round; a chart where no row carries one keeps the two-bar
   * row height it always had.
   */
  c?: number | null;
  cColor?: string;
  cHatched?: boolean;
  /**
   * What the third leg reads as, instead of a number. A leg drawn under `cNominal` is a
   * **verdict, not a measurement** — it has no number to print, and printing the token's
   * own width as one would be the chart inventing a value.
   */
  cText?: string;
}

/** One entry of a caller-supplied legend; see {@link PairedBarsProps.legend}. */
export interface PairedBarsLegendItem {
  id: string;
  label: string;
  /** The swatch's fill. Omitted with `band`, which draws the band's own ground. */
  color?: string;
  /** Draw the hatch over the swatch, for the "not assessed" entry. */
  hatched?: boolean;
  /** Draw the swatch as the marker tick rather than as a block. */
  marker?: boolean;
  /** Draw the swatch as the ±band strip's own ground. */
  band?: boolean;
}

export interface PairedBarsProps {
  rows: PairedBarsRow[];
  /** Two names, or three when the rows carry a third leg. */
  labels: [string, string] | [string, string, string];
  unit: string;
  ariaLabel: string;
  /** The two series' own colours; defaults to neutral grey then the brand accent. */
  colors?: [string, string];
  /** The legend entry for `rows[].marker`; the tick is drawn whether or not this is given. */
  markerLabel?: string;
  /** The marker's ink, defaulting to the chart's own foreground. */
  markerColor?: string;
  /**
   * The unit the pane is focused on (`#intelFocus`, or a map click): its name is drawn
   * bold and both of its bars are outlined, so one row of eleven can be found at a
   * glance without the chart dropping the other ten.
   */
  selectedId?: string | null;
  /**
   * **Signed mode.** Zero sits in the middle of the track and a negative bar grows left
   * from it, so a loss reads as a loss instead of as a short gain. Off by default: every
   * chart written before the scaling round draws two positive quantities side by side,
   * where a centred zero would throw away half the track.
   */
  signed?: boolean;
  /**
   * The run's own stability band, in percent, drawn in signed mode as a light strip either
   * side of zero. It is a ground the bars are read against, never a value of its own, so
   * nothing is scaled to it and it carries no number.
   */
  band?: number | null;
  /**
   * A legend of the caller's own, **replacing** the default two-series one. For a chart
   * whose bars are coloured by something other than which series they belong to, where
   * naming the two series next to a colour they do not have would be a lie.
   */
  legend?: PairedBarsLegendItem[];
  /**
   * The third leg is a **verdict token**, not a reading on the scale: it is drawn at the
   * band's own width, centred on zero, left out of what the scale is computed from, and
   * read out by each row's `cText`. UC2's greening check is pass / fail / nothing to
   * judge, which is a colour and a hatch; giving it a length would be a number the run
   * never served.
   */
  cNominal?: boolean;
  /** The x axis' title, "quantity (unit)", centred under the chart (launch round). */
  xLabel?: string;
  /** The y axis' title, rotated at the left of the label column. */
  yLabel?: string;
  /** Layout width in px; measured from the chart's own box when omitted (#749). */
  w?: number;
  /** A status word per row for the popup ("Positive change"), already translated. */
  statusOf?: (row: PairedBarsRow) => string | undefined;
}

/**
 * The chart's scale — every number it draws, the **markers included**.
 *
 * A marker left out of this is a marker drawn off the end of the track: UC2's practices
 * chart puts the clicked watershed on a row whose two class means are 48 and 50, and
 * Nakambe's own 57 landed at 1.14 × `PB_BAR_W`, on top of the value column's own text.
 * A third reading on the same scale has to be part of what the scale is.
 */
function maxOf(rows: PairedBarsRow[], signed = false, withC = true): number {
  const values: number[] = [];
  for (const r of rows) {
    if (typeof r.a === 'number') values.push(signed ? Math.abs(r.a) : r.a);
    if (typeof r.b === 'number') values.push(signed ? Math.abs(r.b) : r.b);
    if (withC && typeof r.c === 'number') values.push(signed ? Math.abs(r.c) : r.c);
    if (typeof r.marker === 'number') values.push(signed ? Math.abs(r.marker) : r.marker);
  }
  const max = values.length ? Math.max(...values) : 1;
  return max > 0 ? max : 1;
}

/** The chart's own `aria-label` — a plain-text readout of every row, both series, one unit. */
function readout(
  ariaLabel: string,
  rows: PairedBarsRow[],
  labels: readonly string[],
  unit: string,
  markerLabel?: string,
  triple = false,
  nominal = false,
): string {
  const withUnit = (v: number | null | undefined) =>
    v == null ? '-' : `${fmtNum(v, 1)} ${unit}`;
  const parts = rows.map((row) => {
    const legs = [
      `${labels[0]} ${withUnit(row.a)}`,
      `${labels[1]} ${withUnit(row.b)}`,
      ...(triple
        ? [`${labels[2] ?? ''} ${nominal ? (row.cText ?? '-') : withUnit(row.c)}`]
        : []),
    ];
    const marker =
      markerLabel && row.marker != null ? `, ${markerLabel} ${withUnit(row.marker)}` : '';
    /**
     * `note` is the row's own plain words, read **after** its numbers. UC2's country rows
     * put the share of watersheds that passed each check there: the bars draw a mean, and
     * a mean alone does not say how many places are behind it.
     */
    const note = row.note ? `. ${row.note}` : '';
    return `${row.label}: ${legs.join(', ')}${marker}${note}`;
  });
  return `${ariaLabel}: ${parts.join('. ')}`;
}

export function PairedBars({
  rows,
  labels,
  unit,
  ariaLabel,
  colors,
  markerLabel,
  markerColor = MARKER_COLOR,
  selectedId = null,
  signed = false,
  band = null,
  legend,
  cNominal = false,
  xLabel: xLabelProp,
  yLabel: yLabelProp,
  w: wProp,
  statusOf,
}: PairedBarsProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { xLabel, yLabel } = useAxisTitles(xLabelProp, yLabelProp);
  /* The titles' bands come out of the measured width; the columns lay out in what is left. */
  const bands = axisBands(xLabel, yLabel);
  const outerW = useIntelWidth(svgRef, wProp, PB_W + bands.left);
  const W = outerW - bands.left;
  const marks = useChartMarks<string>();
  const rawId = useId();
  /** `useId` spells its ids with colons; a `url(#…)` reference wants none. */
  const hatchId = `pb-hatch-${rawId.replace(/[^A-Za-z0-9_-]/g, '')}`;
  const labs = labels as readonly string[];
  /** Three legs per row, or the two every chart before the scaling round draws. */
  const triple = rows.some(
    (r) => (typeof r.c === 'number' && Number.isFinite(r.c)) || r.cText != null,
  );
  const rowH = triple ? PB_ROW_H + PB_BAR_H + LEG_GAP : PB_ROW_H;
  const pitch = rowH + PB_ROW_GAP;
  // A long name wraps onto a second line and its row grows by that line (launch round,
  // 7 Oct 2026): never an ellipsis.
  const column = rowLabelColumn(
    rows.map((row) => row.label),
    W,
    { min: PB_LABEL_W },
  );
  const lines = column.lines;
  const extras = lines.map((l) => (Math.max(1, l.length) - 1) * ROW_LABEL_PX * LABEL_LINE_H);
  const plotX = column.width + PB_LABEL_GAP;
  const tops = extras.map((_, i) => PB_PAD_TOP + i * pitch + extras.slice(0, i).reduce((a, b) => a + b, 0));
  const h = Math.max(
    PB_MIN_H,
    PB_PAD_TOP + Math.max(rows.length, 1) * pitch + extras.reduce((a, b) => a + b, 0),
  );
  const scale = maxOf(rows, signed, !cNominal);
  const barW = Math.max(20, W - plotX - valueColumnW(rows, W) - PB_VALUE_PAD);
  const [aColor, bColor] = colors ?? [A_COLOR, B_COLOR];
  const hatched =
    rows.some((r) => r.aHatched || r.bHatched || r.cHatched) ||
    (legend ?? []).some((l) => l.hatched);
  /** Signed mode's own geometry: zero at the middle of the track, half a track each way. */
  const halfW = barW / 2;
  const zeroX = plotX + halfW;
  /** One value's x in signed mode, clamped so nothing is drawn past the track's end. */
  const signedX = (v: number) => zeroX + Math.min(1, Math.max(-1, v / scale)) * halfW;
  const bandW = band != null && band > 0 ? Math.min(halfW, (band / scale) * halfW) : 0;

  return (
    <>
      <svg
        ref={svgRef}
        className={`intel-chart pb-chart${marks.rootClass}`}
        width="100%"
        viewBox={`0 0 ${outerW} ${h + bands.bottom}`}
        role="img"
        aria-label={readout(ariaLabel, rows, labs, unit, markerLabel, triple, cNominal)}
        data-testid="paired-bars-chart"
        data-fluid=""
        data-w={outerW}
        onMouseLeave={marks.clear}
      >
        <AxisLabels
          xLabel={xLabel}
          yLabel={yLabel}
          plotLeft={bands.left + plotX}
          plotRight={bands.left + W}
          plotTop={PB_PAD_TOP}
          plotBottom={h}
          height={h + bands.bottom}
        />
        <g transform={bands.left ? `translate(${bands.left} 0)` : undefined} role="list">
        {hatched ? (
          <defs>
            <pattern
              id={hatchId}
              width={4}
              height={4}
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <line x1={0} y1={0} x2={0} y2={4} stroke="#fff" strokeWidth={1.6} />
            </pattern>
          </defs>
        ) : null}
        {signed && bandW > 0 ? (
          <rect
            data-band=""
            x={zeroX - bandW}
            y={0}
            width={bandW * 2}
            height={h}
            fill={BAND_COLOR}
          />
        ) : null}
        {signed ? (
          <line data-zero="" x1={zeroX} x2={zeroX} y1={0} y2={h} stroke="var(--ink-3)" strokeWidth={1} />
        ) : null}
        {rows.map((row, i) => {
          const y = tops[i]! + extras[i]! / 2;
          /** One bar's left edge and width, signed or not. */
          const geom = (v: number | null | undefined) => {
            if (typeof v !== 'number' || !Number.isFinite(v)) return { x: plotX, w: 0 };
            if (!signed) return { x: plotX, w: Math.max(2, (Math.max(v, 0) / scale) * barW) };
            const end = signedX(v);
            const w = Math.max(2, Math.abs(end - zeroX));
            return { x: end < zeroX ? zeroX - w : zeroX, w };
          };
          const selected = row.id === selectedId;
          const focus = selected ? { stroke: 'var(--ink)', strokeWidth: 1.5 } : {};
          const legs = [
            { id: 'a', v: row.a, ink: row.aColor ?? aColor, hatch: row.aHatched },
            { id: 'b', v: row.b, ink: row.bColor ?? bColor, hatch: row.bHatched },
            ...(triple
              ? [{ id: 'c', v: row.c ?? null, ink: row.cColor ?? C_COLOR, hatch: row.cHatched }]
              : []),
          ];
          /** The verdict token's own width: the band's, or a sliver where there is none. */
          const tokenW = bandW > 0 ? bandW * 2 : Math.max(4, barW * 0.06);
          const markerX =
            row.marker == null
              ? null
              : signed
                ? signedX(row.marker)
                : plotX + (Math.max(row.marker, 0) / scale) * barW;
          const withUnit = (v: number | null | undefined) =>
            v == null ? '-' : `${fmtNum(v, 1)} ${unit}`;

          return (
            <g
              key={row.id}
              className={`pb-row mark${marks.isHot(row.id) ? ' is-hot' : ''}`}
              data-row={row.id}
              {...marks.markProps(row.id, () => {
                const status = statusOf?.(row);
                return {
                  label: row.label,
                  value: row.valueText ?? valueOf(row),
                  rows: [
                    ...legs.map((leg, k) => ({
                      label: labs[k] ?? '',
                      value: leg.id === 'c' && cNominal ? (row.cText ?? '-') : withUnit(leg.v),
                      color: leg.ink,
                    })),
                    ...(markerLabel && row.marker != null
                      ? [{ label: markerLabel, value: withUnit(row.marker), color: markerColor }]
                      : []),
                    ...(status ? [{ label: status, value: '' }] : []),
                    ...(row.note ? [{ label: row.note, value: '' }] : []),
                  ],
                };
              })}
            >
              <title>
                {`${row.label}${colonSep()}` +
                  legs
                    .map(
                      (leg, k) =>
                        `${labs[k] ?? ''} ${
                          leg.id === 'c' && cNominal ? (row.cText ?? '-') : withUnit(leg.v)
                        }`,
                    )
                    .join(', ')}
              </title>
              <RowLabel
                className="pb-rowlabel"
                lines={lines[i]!}
                x={0}
                baseline={y + rowH / 2 + 4}
                size={ROW_LABEL_PX}
                textAnchor="start"
                fontWeight={selected ? 700 : undefined}
              />
              {legs.map((leg, k) => {
                // A verdict token belongs only to a row that has a verdict: a row the
                // run said nothing about draws no third leg rather than an empty one.
                if (leg.id === 'c' && cNominal && row.cText == null) return null;
                const { x, w } =
                  leg.id === 'c' && cNominal
                    ? { x: zeroX - tokenW / 2, w: tokenW }
                    : geom(leg.v);
                if (w <= 0) return null;
                const legY = y + k * (PB_BAR_H + LEG_GAP);
                return (
                  <g key={leg.id} data-leg={leg.id}>
                    <rect
                      className="mk"
                      x={x}
                      y={legY}
                      width={w}
                      height={PB_BAR_H}
                      rx={2}
                      fill={leg.ink}
                      {...focus}
                    />
                    {leg.hatch ? (
                      <rect
                        data-hatched=""
                        x={x}
                        y={legY}
                        width={w}
                        height={PB_BAR_H}
                        rx={2}
                        fill={`url(#${hatchId})`}
                      />
                    ) : null}
                  </g>
                );
              })}
              {markerX != null ? (
                <line
                  data-marker=""
                  x1={markerX}
                  x2={markerX}
                  y1={y - 2}
                  y2={y + rowH + 1}
                  stroke={markerColor}
                  strokeWidth={1.5}
                />
              ) : null}
              <text className="pb-num" x={W} y={y + rowH / 2 + 4} textAnchor="end" style={VALUE_HALO}>
                {row.valueText ?? valueOf(row)}
              </text>
            </g>
          );
        })}
        </g>
      </svg>
      <ul className="chart-legend">
        {legend ? (
          legend.map((item) => (
            <li key={item.id} data-legend={item.id}>
              <span
                className={
                  'swatch' +
                  (item.marker ? ' pb-swatch-marker' : '') +
                  (item.band ? ' pb-swatch-band' : '') +
                  (item.hatched ? ' pb-swatch-hatched' : '')
                }
                /**
                 * `backgroundColor`, never the `background` shorthand: the hatched
                 * swatch's stripes are a `background-image` in the stylesheet, and the
                 * shorthand resets every background property it does not name, which
                 * wiped them off the legend while leaving them on the bar.
                 */
                style={
                  item.band
                    ? { backgroundColor: BAND_COLOR }
                    : item.color
                      ? { backgroundColor: item.color }
                      : undefined
                }
              />
              <span className="legend-label">{item.label}</span>
            </li>
          ))
        ) : (
          <>
            <li>
              <span className="swatch" style={{ backgroundColor: aColor }} />
              <span className="legend-label">{labs[0]}</span>
            </li>
            <li>
              <span className="swatch" style={{ backgroundColor: bColor }} />
              <span className="legend-label">{labs[1]}</span>
            </li>
            {markerLabel ? (
              <li data-testid="pb-legend-marker">
                <span className="swatch pb-swatch-marker" style={{ backgroundColor: markerColor }} />
                <span className="legend-label">{markerLabel}</span>
              </li>
            ) : null}
          </>
        )}
      </ul>
    </>
  );
}

export default PairedBars;
